// Lib
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { colorBorders } from '../utils/tableCellFormattingUtils';
import { finishUndoingOrRedoing, redoAction, undoAction } from '../../../utils/undoRedo/undoRedoActions';
import { getIsRedoing, getIsUndoing } from '../../../utils/undoRedo/undoRedoSelector';

/**
 * Custom hook for managing table cell editing side effects
 * Handles syncing props to Handsontable instance and overriding undo/redo functionality
 *
 * @param {Object} props - Hook configuration
 */
export const useTableCellEditingEffects = (props) => {
    const {
        locale,
        filterQuery,
        isReadOnly,
        showTitle,
        showCaption,
        isResizing,
        gridSize,
        isDarkMode,
        isSingleSelected,
        hotTableInstanceRef,
    } = props;

    const dispatch = useDispatch();

    // TODO-TABLES - We'll likely want to handle these within the hot instance itself, rather than Milanote
    const isUndoingOrRedoing = useSelector((state) => getIsUndoing(state) || getIsRedoing(state));
    const dispatchUndoAction = useCallback(() => dispatch(undoAction()), []);
    const dispatchRedoAction = useCallback(() => dispatch(redoAction()), []);
    const dispatchFinishUndoingOrRedoing = useCallback(() => dispatch(finishUndoingOrRedoing()), []);

    /**
     * Update locale in Handsontable instance and re-render
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

        hotTableInstanceRef.current.milanoteProps.locale = locale;
        hotTableInstanceRef.current.render();
    }, [locale]);

    /**
     * Update filterQuery value in the hotTableInstance
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.filterQuery = filterQuery;
        hotTableInstanceRef.current.render();
    }, [filterQuery]);

    /**
     * Update settings for readonly table
     */
    useEffect(() => {
        if (!isReadOnly) return;

        // disable all shortcuts
        hotTableInstanceRef.current?.unlisten();
    }, [isReadOnly]);

    /**
     * Update showTitle value in the hotTableInstance
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.showTitle = showTitle;
    }, [showTitle]);

    /**
     * Update showCaption value in the hotTableInstance
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.showCaption = showCaption;
    }, [showCaption]);

    /**
     * Update isResizing value in the hotTableInstance
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.isResizing = isResizing;
    }, [isResizing]);

    /**
     * Update gridSize and destroy editor when grid size changes
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.gridSize = gridSize;

        // Destroy the editor so that it can be recalculated in new grid size
        requestAnimationFrame(() => {
            if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

            hotTableInstanceRef.current.destroyEditor();
        });
    }, [gridSize]);

    /**
     * Update relevant values with new darkmode setting
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.isDarkMode = isDarkMode;

        // recalculate the coloured cell borders with the new darkmode setting
        const dataArray = hotTableInstanceRef.current.getData();
        for (let row = 0; row < dataArray.length; row++) {
            for (let col = 0; col < dataArray[row].length; col++) {
                colorBorders(hotTableInstanceRef.current, row, col);
            }
        }
    }, [isDarkMode]);

    /**
     * Override Handsontable undo/redo functionality to use Milanote's instead
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        const undoRedoPluginInstance = hotTableInstanceRef.current.getPlugin('undoRedo');
        undoRedoPluginInstance.undo = () => {
            if (hotTableInstanceRef.current.isListening()) {
                dispatchUndoAction();
            }
        };
        undoRedoPluginInstance.redo = () => {
            if (hotTableInstanceRef.current.isListening()) {
                dispatchRedoAction();
            }
        };
    }, []);

    /**
     * isUndoingOrRedoing state persists as true in the state, and causes issues where changes to cell is cleared
     * after an undo/redo operation. So, always toggle this state back to false after an undo/redo operation on table.
     */
    useEffect(() => {
        if (isSingleSelected && isUndoingOrRedoing) dispatchFinishUndoingOrRedoing();
    }, [isSingleSelected, isUndoingOrRedoing]);
};

export default useTableCellEditingEffects;
